import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// REDUX
import { connect } from 'react-redux';
import { navigateToEB, navigateToPC } from 'redux/actions/ModuleStateActions';
import { detailViewOpen } from 'redux/actions/EventSubscriptionActions';
// MATERIAL-UI
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Toolbar from '@mui/material/Toolbar';
import { Box } from '@mui/material';
// COMPONENTS
import AppNavigation from './AppNavigation';
import RightAppBar from './RightAppBar';
// FETCH & CONFIG
import AppFetch from 'fetch/AppFetch';
import AppAuth from 'AppAuth';
import VersionFetch from 'fetch/VersionFetch';
import versionInfo from 'version.json';
import { moduleConfig } from 'lib/config/appConstants';
import AppConfig from 'appConfig';
// LIB
import { heapIdentify } from 'lib/heapHelpers';
import { fetchAlertsCount } from 'redux/actions/thunks/AlertsThunk';

const appBarContainerSx = (theme) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
});

const AppHeader = (props) => {
  const { module, onAppError, alertsCount, onDetailViewOpen, onRefetchAlertsCount } = props;

  const [latestVersionInfo, setLatestVersionInfo] = useState(versionInfo);

  const userInfo = React.useMemo(() => {
    return AppAuth.loggedIn() ? AppFetch.fetchUserInfo() : null;
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (isMounted && userInfo) {
      heapIdentify(userInfo);
    }

    VersionFetch.fetchLatestVersion().then((latestVersionInfo) => {
      if (isMounted) {
        setLatestVersionInfo(latestVersionInfo);
      }
    });

    if (isMounted) {
      onRefetchAlertsCount(module);
    }

    return () => {
      isMounted = false;
    };
  }, [userInfo, module, onRefetchAlertsCount]);

  if (!props) {
    return null;
  }

  return (
    <Box sx={appBarContainerSx}>
      <AppBar position="static" enableColorOnDark>
        <Toolbar variant="dense">
          <AppNavigation module={module} />
          <Box sx={{ flexGrow: 1 }} />
          <RightAppBar
            onAppError={onAppError}
            userInfo={userInfo}
            module={module}
            alertsCount={alertsCount}
            onDetailViewOpen={onDetailViewOpen}
          />
        </Toolbar>
      </AppBar>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{ top: 90 }}
        open={
          !AppConfig.enableAutoRefreshNewVersion &&
          versionInfo?.version !== latestVersionInfo?.version
        }
        autoHideDuration={6000}
        message={<span>A new version of miEdge is available, please refresh to upgrade</span>}
        action={[
          <Button
            key="undo"
            color="secondary"
            size="small"
            onClick={() => window.location.reload()}
          >
            REFRESH
          </Button>,
        ]}
      />
    </Box>
  );
};

AppHeader.propTypes = {
  onAppError: PropTypes.func.isRequired,
  module: PropTypes.oneOf([moduleConfig.PC.shortName, moduleConfig.EB.shortName]).isRequired,
  detailViewOpen: PropTypes.bool,
  onRequestEB: PropTypes.func.isRequired,
  onRequestPC: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    module: state.ModuleState.module,
    alertsCount: state.EventSubscriptionState.alertsCount,
    selectedEntity: state.EntityDetails.selectedEntity,
  };
}

const mapDispatchToProps = (dispatch) => ({
  onRequestEB: () => {
    dispatch(navigateToEB());
  },
  onRequestPC: () => {
    dispatch(navigateToPC());
  },
  onDetailViewOpen: (open) => {
    dispatch(detailViewOpen(open));
  },
  onRefetchAlertsCount: (module) => {
    const criterias = {
      application: module?.toLowerCase(),
    };
    dispatch(fetchAlertsCount(criterias));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AppHeader);
